// Helpers around mp4box.js for the recorder-mode canvas-video pipeline.
// Pure synchronous transforms — no mp4box runtime, easy to unit-test.

/**
 * Build the AVC config (avcC box payload) bytes that VideoDecoder.configure
 * needs as `description` for H.264 streams. Input is the parsed avcC struct
 * mp4box.js exposes on `track.avcC`.
 *
 * Byte layout per ISO/IEC 14496-15:
 *   1B configurationVersion (=1)
 *   1B AVCProfileIndication
 *   1B profile_compatibility
 *   1B AVCLevelIndication
 *   1B 0xFC | (lengthSizeMinusOne & 0x03)
 *   1B 0xE0 | (numSPS & 0x1F)
 *   for each SPS: 2B BE length, N B nalu
 *   1B numPPS
 *   for each PPS: 2B BE length, N B nalu
 */
// Different mp4box.js versions expose the SPS/PPS NALU bytes under
// different field names: older builds use `nalu`, newer (v2.x) use `data`
// alongside an explicit `length`. Accept both shapes.
function naluBytes(entry) {
  const buf = entry.data || entry.nalu;
  if (!buf) {
    throw new Error("extractAvcDescription: NALU bytes missing (no .data or .nalu)");
  }
  return buf;
}

export function extractAvcDescription(avcC) {
  if (!avcC || avcC.configurationVersion == null) {
    throw new Error("extractAvcDescription: avcC is missing or malformed");
  }
  const sps = Array.isArray(avcC.SPS) ? avcC.SPS : [];
  const pps = Array.isArray(avcC.PPS) ? avcC.PPS : [];
  let totalLen = 7; // header(5) + numSPS byte(1) + numPPS byte(1)
  for (const s of sps) totalLen += 2 + naluBytes(s).byteLength;
  for (const p of pps) totalLen += 2 + naluBytes(p).byteLength;

  const out = new Uint8Array(totalLen);
  let i = 0;
  out[i++] = avcC.configurationVersion & 0xff;
  out[i++] = avcC.AVCProfileIndication & 0xff;
  out[i++] = avcC.profile_compatibility & 0xff;
  out[i++] = avcC.AVCLevelIndication & 0xff;
  out[i++] = 0xfc | (avcC.lengthSizeMinusOne & 0x03);
  out[i++] = 0xe0 | (sps.length & 0x1f);
  for (const s of sps) {
    const bytes = naluBytes(s);
    const len = bytes.byteLength;
    out[i++] = (len >> 8) & 0xff;
    out[i++] = len & 0xff;
    out.set(bytes, i);
    i += len;
  }
  out[i++] = pps.length & 0xff;
  for (const p of pps) {
    const bytes = naluBytes(p);
    const len = bytes.byteLength;
    out[i++] = (len >> 8) & 0xff;
    out[i++] = len & 0xff;
    out.set(bytes, i);
    i += len;
  }
  return out;
}

/**
 * Convert mp4box.js sample timing (cts, duration in track-timescale units)
 * to microseconds, the unit WebCodecs.EncodedVideoChunk expects.
 */
export function sampleTimingToMicros(sample, trackTimescale) {
  return {
    timestamp: Math.round((sample.cts * 1e6) / trackTimescale),
    duration: Math.round((sample.duration * 1e6) / trackTimescale),
  };
}
